import type { ConversationData } from '../components/sections-content/ConversationContent';

const CONVERSATION_CRITICALITY_MAP: Record<string, string> = {
  'criticality-none': '0',
  'criticality-low': '1',
  'criticality-medium': '2',
  'criticality-high': '3',
};

const CONVERSATION_STATUS_MAP: Record<string, string> = {
  'status-ignored': 'Ignorado',
  'status-pending': 'Pendente',
  'status-open': 'Aberto',
  'status-waiting': 'Em espera',
  'status-replied': 'Respondido',
  'status-closed': 'Fechado',
};

const selectedKeys = (map: Record<string, string>): string[] =>
  Object.entries(map)
    .filter(([, action]) => action === 'add')
    .map(([key]) => key);

const isAllSelected = (selectedCount: number, availableCount: number | undefined): boolean =>
  availableCount !== undefined && selectedCount > 0 && selectedCount === availableCount;

const getEffectiveValues = <T,>(
  selected: T[],
  availableCount: number | undefined,
  defaults: T[],
  transform?: (item: T) => T,
): T[] => {
  const shouldUseSelected = selected.length > 0 && !isAllSelected(selected.length, availableCount);
  if (!shouldUseSelected) return defaults;
  return transform ? selected.map(transform) : selected;
};

const parsePageValues = (pages: string[]) => {
  return pages.map((pageKey) => {
    const separatorIndex = pageKey.indexOf('-');
    if (separatorIndex < 0) return { channel: '', pageId: pageKey };
    return {
      channel: pageKey.slice(0, separatorIndex).toLowerCase(),
      pageId: pageKey.slice(separatorIndex + 1),
    };
  });
};

const addConversationFields = (
  payload: Record<string, unknown>,
  selectedCriticality: string[],
  selectedStatus: string[],
  selectedRespondents: string[],
  selectedRouting: string[],
  routingNameById: Record<string, string>,
  availableCounts?: {
    criticality?: number;
    status?: number;
    respondents?: number;
    routing?: number;
  },
): void => {
  if (selectedCriticality.length > 0 && !isAllSelected(selectedCriticality.length, availableCounts?.criticality)) {
    payload.sac_critical_level = { value: selectedCriticality.map((item) => CONVERSATION_CRITICALITY_MAP[item] ?? item) };
  }

  if (selectedStatus.length > 0 && !isAllSelected(selectedStatus.length, availableCounts?.status)) {
    payload.sac_status = { value: selectedStatus.map((item) => CONVERSATION_STATUS_MAP[item] ?? item) };
  }

  if (selectedRespondents.length > 0 && !isAllSelected(selectedRespondents.length, availableCounts?.respondents)) {
    payload.sac_replier = { value: selectedRespondents.map((item) => item.replace(/^respondent-/, '')) };
  }

  if (selectedRouting.length > 0 && !isAllSelected(selectedRouting.length, availableCounts?.routing)) {
    const routingValues = selectedRouting.map((item) => item.replace(/^routing-/, ''));
    payload.routing_id = {
      value: routingValues,
      routers: routingValues.map((routingId) => ({ routingId, name: routingNameById[routingId] ?? routingId })),
    };
  }
};

export const buildConversationPayload = (
  data: ConversationData,
  routingNameById: Record<string, string> = {},
  defaults?: { channels: string[]; pages: string[]; interactions: string[] },
  availableCounts?: {
    status?: number;
    respondents?: number;
    criticality?: number;
    routing?: number;
    channels?: number;
    pages?: number;
    interactions?: number;
  },
): Record<string, unknown> => {
  const selectedChannels = selectedKeys(data.channels);
  const selectedPages = selectedKeys(data.pages);
  const selectedInteractions = selectedKeys(data.interactions);

  const effectiveChannels = getEffectiveValues(
    selectedChannels,
    availableCounts?.channels,
    defaults?.channels || [],
    (ch) => ch.toLowerCase(),
  );
  const effectivePages = getEffectiveValues(selectedPages, availableCounts?.pages, defaults?.pages || []);
  const effectiveInteractions = getEffectiveValues(selectedInteractions, availableCounts?.interactions, defaults?.interactions || []);

  const parsedPageValues = parsePageValues(effectivePages);
  const pageChannels = parsedPageValues.map((page) => page.channel).filter(Boolean);
  const pageIds = parsedPageValues.map((page) => page.pageId).filter(Boolean);

  const payload: Record<string, unknown> = {};
  const channelsToEncode = pageChannels.length > 0 ? pageChannels : effectiveChannels;

  if (channelsToEncode.length > 0) payload.channels = { value: channelsToEncode };
  if (pageIds.length > 0) payload.pages = { value: pageIds };
  if (effectiveInteractions.length > 0) payload.sac_type = { value: effectiveInteractions };

  const selectedRespondents = data.respondentTouched ? selectedKeys(data.respondent) : [];
  const selectedRouting = data.routingTouched ? selectedKeys(data.routing) : [];

  addConversationFields(
    payload,
    selectedKeys(data.criticality),
    selectedKeys(data.status),
    selectedRespondents,
    selectedRouting,
    routingNameById,
    availableCounts,
  );

  if (data.conversationId.trim().length > 0) {
    const ids = data.conversationId.split(',').map((id) => id.trim()).filter(Boolean);
    if (ids.length > 0) {
      payload.sac_call_ids = { value: ids };
    }
  }

  const hasPublicationTypeSelection = data.publicationType.organic || data.publicationType.darkPost;
  if (hasPublicationTypeSelection) {
    payload.dark_post = { value: data.publicationType.darkPost };
  }

  return payload;
};
